<template>
  <content-modal
    ref="MyDriveResourceNodeEditModal"
    class="my-drive-folder-edit-modal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :auto-hide-off="autoHideOff"
    :size="size"
    @modal-hide-request="hideRequest"
  >
    <template #title>{{ title }}</template>
    <template #content>
      <modal-content>
        <template #content>
          <cv-form @submit.prevent>
            <dl class="field-set folder-edit">
              <dt class="field-label">{{ labelName }}</dt>
              <dt class="field-input">
                <text-input
                  ref="titleTextInput"
                  class="folder-name-text-input"
                  :visible="visible"
                  :mandatory="true"
                  :value="params.resourceNodeTitle"
                  :character-limit="resourceCharacterLimit"
                  :error-message="resourceTitleError"
                  :text-id="id"
                  @input="handleResourceNameChange"
                />
              </dt>
            </dl>
          </cv-form>
        </template>
        <template #tools>
          <cv-button class="cancel" kind="tertiary" size="field" @click="hideRequest">{{
            t("button_cancel")
          }}</cv-button>
          <fetch-data-button
            :kind="dataUploadButton.kind"
            :size="dataUploadButton.size"
            :loading="dataUploadButton.loading"
            :error="dataUploadButton.error"
            :character-limit="resourceCharacterLimit"
            @click="handleConfirmationClick"
            @reset-error-state="handleFailReset"
          >
            <span v-if="dataUploadButton.error">{{ t("button_state_error") }}</span>
            <span v-else>{{ primaryButton }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script lang="ts" setup>
import DOMUtils from "@utils/dom.utils";
import randomUtils from "@utils/random.utils";
import { isDefined, isEmpty } from "@utils/var.check";
import MyDriveApi from "@apis/my.drive.api";
import { RESOURCE_TYPE } from "@plugins/common/constant/resource.constant.ts";
import myDriveEventBus from "@modules/my-drive/service/event-bus";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import { ref, computed, useTemplateRef } from "vue";
import { useModal } from "@/plugins/modal/composables/modal-instance";
import { useI18n } from "vue-i18n";
import { useDirty } from "@plugins/common/composables/page-dirty";

const { t } = useI18n();
const modalHandler = useModal();

// Props
interface Params {
  id: number;
  name: string;
}

const props = defineProps<{
  params: Params;
}>();

// Data
const id = ref(randomUtils.randomId());
const closeAriaLabel = ref("Close");
const size = ref("small");
const autoHideOff = ref(true);
const visible = ref(true);
const titleTextInput = useTemplateRef("titleTextInput");
const dataUploadButton = ref({
  kind: "primary",
  size: "field",
  error: false,
  loading: false,
});
const resourceCharacterLimit = ref(250);
const updatedName = ref("");
const resourceTitleError = ref("");

// Computed
const dirtyId = computed(() => {
  return id.value;
});

const { promptComponentDirty, setDirty, removeDirty } = useDirty(dirtyId);

const labelName = computed(() => {
  return props.params.resourceType === RESOURCE_TYPE.FOLDER ? t("folder_name") : t("module_title");
});

const isRenameFolder = computed(() => {
  return props.params.isRenamingResourceNode && props.params.resourceType === RESOURCE_TYPE.FOLDER;
});

const isAddNewFolder = computed(() => {
  return !props.params.isRenamingResourceNode && props.params.resourceType === RESOURCE_TYPE.FOLDER;
});

const isRenameLesson = computed(() => {
  return props.params.isRenamingResourceNode && props.params.resourceType === RESOURCE_TYPE.LESSON;
});

const title = computed(() => {
  if (isRenameFolder.value) {
    return t("rename_folder");
  } else if (isAddNewFolder.value) {
    return t("add_new_folder");
  } else if (isRenameLesson.value) {
    return t("rename_lesson");
  } else return "";
});

const primaryButton = computed(() => {
  if (props.params.isRenamingResourceNode) {
    return t("rename");
  } else {
    return t("add_new_folder");
  }
});

// Methods
const hide = function () {
  // restore previous scrollability
  DOMUtils.removeClass("body", "bx--body--with-modal-open");
  modalHandler.removeAll();
  EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
};

const hideRequest = function (event) {
  // to prevent close modal upon clicking anywhere outside the modal container
  if (event["cv:reason"] !== "external-click") {
    promptComponentDirty().then((response) => {
      if (!response) {
        return;
      }
      hide();
    });
  }
};

const handleResourceNameChange = function (value) {
  updatedName.value = value;
  setDirty(id.value);
};

const validate = function () {
  clearErrorMessage();
  return titleTextInput.value.validate();
};

const clearErrorMessage = function () {
  resourceTitleError.value = "";
};

const handleConfirmationClick = function () {
  if (dataUploadButton.value.loading || dataUploadButton.value.error) {
    return;
  }
  if (validate()) {
    if (isRenameFolder.value || isRenameLesson.value) {
      renameResource();
    } else if (isAddNewFolder.value) {
      createNewFolder();
    }
  }
};

const createNewFolder = function () {
  dataUploadButton.value.loading = true;
  MyDriveApi.createFolder({
    //OK
    name: updatedName.value,
    parentFolderUuid: isEmpty(props.params.folderUuid) ? "" : props.params.folderUuid,
    fromListing: true,
    currentPage: props.params.currentPage,
  }).then((response) => {
    if (response.code !== "ok") {
      handleFail();
      return;
    }
    dataUploadButton.value.loading = false;
    removeDirty(id.value);
    myDriveEventBus.$emit("resourceNodeUpdated");
    hide();
  }, handleFail);
};

const handleFail = function () {
  dataUploadButton.value.loading = false;
  dataUploadButton.value.error = true;
};

const handleFailReset = function () {
  dataUploadButton.value.error = false;
};

const renameResource = function () {
  dataUploadButton.value.loading = true;
  MyDriveApi.renameResourceNode(props.params.resourceNodeUuid, updatedName.value).then(
    (response) => {
      if (response.code !== "ok") {
        handleFail();
        return;
      }
      dataUploadButton.value.loading = false;
      removeDirty(id.value);
      myDriveEventBus.$emit("resourceNodeUpdated");
      hide();
    },
    handleFail,
  );
};

// Created
if (isDefined(props.params.resourceNodeTitle)) {
  updatedName.value = props.params.resourceNodeTitle;
}
</script>
